/**
 * documents.service.ts
 *
 * Typed API call wrappers for the documents domain.
 * All methods use the shared apiClient (Axios instance with JWT interceptor).
 * Returns unwrapped data from the response envelope — callers never see Axios wrappers.
 */
import { apiClient } from './apiClient';
import type { PaginatedResponse, ApiResponse } from '@/types/api.types';

// ─── Domain types (aligned with server documents.types.ts) ───────────────────

export type DocumentFormat = 'pdf' | 'docx' | 'doc' | 'html' | 'image' | 'other';

export type SigningStatus = 'unsigned' | 'pending' | 'completed' | 'declined' | 'voided';

export interface Document {
  documentId: number;
  title: string;
  description: string | null;
  fileName: string | null;
  thumbNail: string | null;
  format: DocumentFormat;
  categoryId: number | null;
  categoryName: string | null;
  accountId: number;
  active: boolean;
  sortOrder: number | null;
  patientId: number | null;
  patientName: string | null;
  signingStatus: SigningStatus;
  envelopeId: string | null;
  notes: string | null;
  dateUploaded: string | null;
  downloadUrl: string;
}

export interface DocumentCategory {
  categoryId: number;
  categoryName: string;
  description: string | null;
  active: boolean;
  accountId: number;
}

export interface PacketDocument {
  documentId: number;
  title: string;
  format: DocumentFormat;
  sortOrder: number | null;
}

export interface DocumentPacket {
  packetId: number;
  name: string;
  description: string | null;
  active: boolean;
  sortOrder: number | null;
  accountId: number;
  documents: PacketDocument[];
}

export interface DocuSignEnvelope {
  docusignId: number;
  documentId: number;
  patientId: number | null;
  planId: number | null;
  envelopeId: string;
  status: SigningStatus;
  signerEmail: string | null;
  signerName: string | null;
  accountId: number;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
}

export interface CreateCategoryPayload {
  categoryName: string;
  description?: string;
  active?: boolean;
}

export interface UpdateCategoryPayload {
  categoryName?: string;
  description?: string | null;
  active?: boolean;
}

// ─── Request param types ──────────────────────────────────────────────────────

export interface DocumentFilters {
  patientId?: number;
  categoryId?: number;
  format?: DocumentFormat;
  signed?: boolean;
  dateFrom?: string;
  dateTo?: string;
  search?: string;
}

export interface DocumentPagination {
  page?: number;
  perPage?: number;
  sort?: string;
  order?: 'asc' | 'desc';
}

export interface UploadDocumentMetadata {
  title: string;
  categoryId?: number;
  notes?: string;
  format?: DocumentFormat;
  planId?: number;
}

export interface UpdateDocumentPayload {
  title?: string;
  categoryId?: number;
  notes?: string;
  description?: string;
  active?: boolean;
}

// ─── Service methods ──────────────────────────────────────────────────────────

/**
 * Fetch a paginated list of documents, optionally filtered.
 */
export function getDocuments(
  filters: DocumentFilters = {},
  pagination: DocumentPagination = {},
): Promise<PaginatedResponse<Document>> {
  return apiClient
    .get<PaginatedResponse<Document>>('/documents', {
      params: { ...filters, ...pagination },
    })
    .then((r) => r.data);
}

/**
 * Fetch the full document record by ID (metadata only).
 */
export function getDocument(id: number): Promise<Document> {
  return apiClient
    .get<ApiResponse<Document>>(`/documents/${id}`)
    .then((r) => r.data.data);
}

/**
 * Upload a new document file with metadata.
 * Sends as multipart/form-data. Reports upload progress via onProgress callback.
 */
export function uploadDocument(
  patientId: number | null,
  file: File,
  metadata: UploadDocumentMetadata,
  onProgress?: (percent: number) => void,
): Promise<Document> {
  const form = new FormData();
  form.append('file', file);
  form.append('title', metadata.title);

  if (patientId !== null) {
    form.append('patientId', String(patientId));
  }
  if (metadata.categoryId !== undefined) {
    form.append('categoryId', String(metadata.categoryId));
  }
  if (metadata.notes !== undefined) {
    form.append('notes', metadata.notes);
  }
  if (metadata.format !== undefined) {
    form.append('format', metadata.format);
  }
  if (metadata.planId !== undefined) {
    form.append('planId', String(metadata.planId));
  }

  return apiClient
    .post<ApiResponse<Document>>('/documents', form, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (event) => {
        if (onProgress && event.total && event.total > 0) {
          const percent = Math.round((event.loaded * 100) / event.total);
          onProgress(percent);
        }
      },
    })
    .then((r) => r.data.data);
}

/**
 * Update document metadata (partial update supported).
 */
export function updateDocument(id: number, metadata: UpdateDocumentPayload): Promise<Document> {
  return apiClient
    .put<ApiResponse<Document>>(`/documents/${id}`, metadata)
    .then((r) => r.data.data);
}

/**
 * Soft-delete a document.
 */
export function deleteDocument(id: number): Promise<void> {
  return apiClient.delete(`/documents/${id}`).then(() => undefined);
}

/**
 * Download the document file and return an object URL suitable for <a href>.
 * Caller is responsible for calling URL.revokeObjectURL() when done.
 */
export function downloadDocument(id: number): Promise<string> {
  return apiClient
    .get<Blob>(`/documents/${id}/download`, { responseType: 'blob' })
    .then((r) => URL.createObjectURL(r.data));
}

/**
 * Initiate a DocuSign signing envelope for a document.
 */
export function initiateDocuSign(
  id: number,
  signerEmail: string,
  signerName: string,
  opts?: { patientId?: number; planId?: number; anchorStrings?: string[] },
): Promise<DocuSignEnvelope> {
  return apiClient
    .post<ApiResponse<DocuSignEnvelope>>(`/documents/${id}/sign`, {
      signerEmail,
      signerName,
      ...opts,
    })
    .then((r) => r.data.data);
}

/**
 * Check / refresh the DocuSign signing status for a document.
 */
export function checkSigningStatus(id: number): Promise<DocuSignEnvelope> {
  return apiClient
    .get<ApiResponse<DocuSignEnvelope>>(`/documents/${id}/sign/status`)
    .then((r) => r.data.data);
}

/**
 * Send all documents in a packet to a patient signer via DocuSign.
 * Returns the array of created envelope records.
 */
export function sendPacket(
  packetId: number,
  patientId: number,
  opts: { signerEmail: string; signerName: string; planId?: number },
): Promise<DocuSignEnvelope[]> {
  return apiClient
    .post<ApiResponse<DocuSignEnvelope[]>>(`/documents/packets/${packetId}/send`, {
      patientId,
      ...opts,
    })
    .then((r) => r.data.data);
}

/**
 * Fetch all document categories for the account.
 */
export function getCategories(): Promise<DocumentCategory[]> {
  return apiClient
    .get<ApiResponse<DocumentCategory[]>>('/documents/categories')
    .then((r) => r.data.data);
}

/**
 * Create a new document category.
 */
export function createCategory(data: CreateCategoryPayload): Promise<DocumentCategory> {
  return apiClient
    .post<ApiResponse<DocumentCategory>>('/documents/categories', data)
    .then((r) => r.data.data);
}

/**
 * Update a document category.
 */
export function updateCategory(
  id: number,
  data: UpdateCategoryPayload,
): Promise<DocumentCategory> {
  return apiClient
    .put<ApiResponse<DocumentCategory>>(`/documents/categories/${id}`, data)
    .then((r) => r.data.data);
}

/**
 * Soft-delete a document category.
 */
export function deleteCategory(id: number): Promise<void> {
  return apiClient.delete(`/documents/categories/${id}`).then(() => undefined);
}

/**
 * Fetch all document packets for the account (including their document entries).
 */
export function getPackets(): Promise<DocumentPacket[]> {
  return apiClient
    .get<ApiResponse<DocumentPacket[]>>('/documents/packets')
    .then((r) => r.data.data);
}

/**
 * Fetch a single document packet by ID.
 */
export function getPacket(id: number): Promise<DocumentPacket> {
  return apiClient
    .get<ApiResponse<DocumentPacket>>(`/documents/packets/${id}`)
    .then((r) => r.data.data);
}

// ─── Packet CUD ──────────────────────────────────────────────────────────────

export interface CreatePacketPayload {
  name: string;
  description?: string;
  active?: boolean;
  documentIds?: number[];
}

export interface UpdatePacketPayload {
  name?: string;
  description?: string | null;
  active?: boolean;
  documentIds?: number[];
}

/**
 * Create a new document packet.
 */
export function createPacket(data: CreatePacketPayload): Promise<DocumentPacket> {
  return apiClient
    .post<ApiResponse<DocumentPacket>>('/documents/packets', data)
    .then((r) => r.data.data);
}

/**
 * Update a document packet.
 */
export function updatePacket(id: number, data: UpdatePacketPayload): Promise<DocumentPacket> {
  return apiClient
    .put<ApiResponse<DocumentPacket>>(`/documents/packets/${id}`, data)
    .then((r) => r.data.data);
}

/**
 * Soft-delete a document packet.
 */
export function deletePacket(id: number): Promise<void> {
  return apiClient.delete(`/documents/packets/${id}`).then(() => undefined);
}
